import { z } from 'zod';

/**
 * Zod schemas at the HTTP trust boundary for cohort TanStack reads.
 *
 * Tolerant by design: string fields default to '', number fields to 0,
 * `latestStatus` is `string` rather than enum so a new server-side status
 * doesn't blank the page. Required fields are limited to the IDs we
 * need to render anything at all.
 */

const tolerantString = z
  .string()
  .nullish()
  .transform((v) => v ?? '');

const tolerantNumber = z
  .number()
  .nullish()
  .transform((v) => v ?? 0);

const tolerantBoolean = z
  .boolean()
  .nullish()
  .transform((v) => v ?? false);

const userTransactionSchema = z
  .object({
    userId: tolerantString,
    firstName: tolerantString,
    lastName: tolerantString,
    transactionDate: tolerantString.optional(),
  })
  .passthrough();

export const cohortSchema = z
  .object({
    // The cohort-profile / list endpoints serialize the id as
    // ``cohortProfileId`` (canonical backend name since 2024-12); they do NOT
    // send ``cohortId``. Keep ``cohortId`` optional so the parse doesn't
    // soft-fail to the raw response on every fetch — consumers read
    // ``cohortProfileId`` (see useCohortProfileQuery's transform). (OR-6177)
    cohortId: z.string().optional(),
    cohortProfileId: z.string().optional(),
    cohortName: tolerantString,
    cohortDescription: tolerantString,
    latestCohortVersionId: tolerantString,
    latestStatus: tolerantString,
    // Backend-derived unified asset-status vocabulary (Draft / Processing /
    // Ready / Error) from ``cohort_to_asset_status``; additive alongside the raw
    // ``latestStatus``. Consumers render it directly.
    assetStatus: z.string().optional(),
    latestSubjectCount: tolerantNumber,
    // Use plain optional strings (no nullish transform) so consumers can
    // distinguish "field present but empty" from "field absent" — relied on
    // by the prevalence panel's dataset-name label fallback.
    latestDatasetReleaseId: z.string().optional(),
    latestDatasetReleaseDisplayName: z.string().optional(),
    // List-endpoint payload also carries these; tolerant defaults keep the
    // schema's inferred type aligned with the wider `Cohort` interface so
    // `useCohortListQuery` can return `CohortFromApi[]` without casting.
    projectId: tolerantString,
    organizationId: tolerantString,
    organizationName: tolerantString,
    isExportInProgress: tolerantBoolean,
    createdBy: userTransactionSchema.optional(),
    modifiedBy: userTransactionSchema.optional(),
  })
  .passthrough();

export const objectResponse = <T extends z.ZodTypeAny>(inner: T) =>
  z.object({ data: inner }).passthrough();

export type CohortFromApi = z.infer<typeof cohortSchema>;

/** One analytical dataset whose latest version references the cohort. */
export const analyticalDatasetUsageRefSchema = z
  .object({
    analyticalDatasetProfileId: z.string(),
    name: tolerantString,
    // The AD's own latest status (DRAFT/PROCESSING/PROCESSED/ERROR) — used to
    // block cohort *reprocessing* (not editing) while a dependent AD is
    // actively PROCESSING. Optional/tolerant: older payloads may omit it.
    latestStatus: z.string().optional(),
  })
  .passthrough();

/**
 * `/cohort-profile/<id>/usage` payload. `isUsed`/`usageCount` are derived
 * server-side from `analyticalDatasets`; we keep them tolerant so a partial
 * payload still renders, and treat the list as the source of truth.
 */
export const cohortUsageSchema = z
  .object({
    cohortProfileId: tolerantString,
    isUsed: tolerantBoolean,
    usageCount: tolerantNumber,
    analyticalDatasets: z
      .array(analyticalDatasetUsageRefSchema)
      .nullish()
      .transform((v) => v ?? []),
  })
  .passthrough();

export type AnalyticalDatasetUsageRefFromApi = z.infer<typeof analyticalDatasetUsageRefSchema>;
export type CohortUsageFromApi = z.infer<typeof cohortUsageSchema>;
